import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Recurrence,
  selectMeetingFromTime,
  selectRecurrence,
  selectRecurrenceEndDate,
  setRecurrence,
  setRecurrenceEndDate
} from "../../models/meeting-room-booking-dialog.duck";
import {
  DropDownListItem,
  ReactSelectDropDownList
} from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import Colours from '@shared/ui/atoms/Colours';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import styled from "styled-components";
import { DateInputField } from '@shared/ui/molecules/DateInputField';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import FullScreenDateRangeSelector, {
  SelectionType
} from '@shared/ui/molecules/calendar/FullScreenDateRangeSelector';
import { Moment } from "moment";
import { warningNotification } from '@shared/store/notification.duck';
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';

export function MeetingRecurrenceSection() {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const recurrence = useSelector(selectRecurrence);
  const startDateTime = useSelector(selectMeetingFromTime);
  const recurrenceEndDate = useSelector(selectRecurrenceEndDate);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const maxEndDate = useMemo(() => {
    return startDateTime ? startDateTime.clone().add(6, 'months') : undefined;
  }, [startDateTime]);

  const options: DropDownListItem[] = useMemo(() => [
    { value: '', label: t('room-booking-dialog.no-recurrence') },
    { value: Recurrence.Daily, label: t('room-booking-dialog.daily') },
    { value: Recurrence.Weekly, label: t('room-booking-dialog.weekly') },
    { value: Recurrence.Fortnightly, label: t('room-booking-dialog.fortnightly') },
    { value: Recurrence.AbsoluteMonthly, label: t('room-booking-dialog.monthly') },
  ], [t]);

  const selectedItem = useMemo(() => {
    return options.find(o => o.value === (recurrence || '')) || options[0];
  }, [recurrence, options]);

  const onItemSelected = (selection: DropDownListItem) => {
    dispatch(setRecurrence(selection.value || undefined));
  };

  const onEndDateSelected = (date: Moment) => {
    if (recurrence === Recurrence.RelativeYearly) {
      dispatch(setRecurrenceEndDate(date));
      return;
    }

    if (maxEndDate && date.isAfter(maxEndDate)) {
      dispatch(warningNotification('Cannot book more than 6 months in the future'));
      dispatch(setRecurrenceEndDate(maxEndDate));
    } else {
      dispatch(setRecurrenceEndDate(date));
    }
  };

  const recurrenceSummary = useMemo(() => {
    if (!recurrence || !startDateTime) return '';
    const dayName = startDateTime.format('dddd');
    const dayOfMonth = startDateTime.format('Do');

    const frequency = recurrence === Recurrence.Daily ? t('room-booking-dialog.daily')
      : recurrence === Recurrence.Weekly ? t('room-booking-dialog.weekly')
      : recurrence === Recurrence.Fortnightly ? t('room-booking-dialog.fortnightly')
      : recurrence === Recurrence.AbsoluteMonthly ? t('room-booking-dialog.monthly')
      : t('room-booking-dialog.yearly');

    const isMonthly = recurrence === Recurrence.AbsoluteMonthly;
    const isDaily = recurrence === Recurrence.Daily;
    const isFortnightly = recurrence === Recurrence.Fortnightly;

    if (recurrenceEndDate) {
      const endDateFormatted = recurrenceEndDate.format('D MMMM YYYY');
      if (isDaily) return t('room-booking-dialog.recurrence-summary-daily', { endDate: endDateFormatted });
      if (isMonthly) return t('room-booking-dialog.recurrence-summary-monthly', { dayOfMonth, endDate: endDateFormatted });
      if (isFortnightly) return t('room-booking-dialog.recurrence-summary-fortnightly', { dayName, endDate: endDateFormatted });
      return t('room-booking-dialog.recurrence-summary', { frequency, dayName, endDate: endDateFormatted });
    }

    if (isDaily) return t('room-booking-dialog.recurrence-summary-daily-single');
    if (isMonthly) return t('room-booking-dialog.recurrence-summary-monthly-single', { dayOfMonth });
    if (isFortnightly) return t('room-booking-dialog.recurrence-summary-fortnightly-single', { dayName });
    return t('room-booking-dialog.recurrence-summary-single', { frequency, dayName });
  }, [recurrence, startDateTime, recurrenceEndDate, t]);

  return (
    <Container>
      <BodySmall colour={Colours.darkGrey} weight={600} style={{marginBottom: 4}}>{t('room-booking-dialog.recurrence')}</BodySmall>
      <ReactSelectDropDownList
        items={options}
        selectedItem={selectedItem}
        onItemSelected={onItemSelected}
      />
      {recurrence && (
        <EndDateRow>
          <BodySmall colour={Colours.darkGrey} weight={600}>{t('room-booking-dialog.or-until')}</BodySmall>
          <DateInputField onClick={() => setIsDatePickerOpen(true)}
                          placeholder="textinput.add-date"
                          value={recurrenceEndDate ? recurrenceEndDate.format('D MMMM YYYY') : ''}
                          icon={IconTypes.Calendar}
                          style={{margin: 0, padding: 0, flex: 1}}
                          editableOnly={true} />
          <FullScreenDateRangeSelector open={isDatePickerOpen}
                                       onClose={() => setIsDatePickerOpen(false)}
                                       selectionType={SelectionType.SingleDate}
                                       onSingleDateSelect={onEndDateSelected}
                                       initialDate={startDateTime ?? undefined}
                                       startDate={recurrenceEndDate ?? startDateTime ?? null} />
        </EndDateRow>
      )}
      {recurrenceSummary && (
        <BodySmall colour={Colours.darkGrey} style={{marginTop: 6, fontStyle: 'italic', textAlign: 'right'}}>
          {recurrenceSummary}
        </BodySmall>
      )}
      <HorizontalSpacer />
    </Container>
  );
}

const Container = styled.div`
    margin-bottom: 16px;
`

const EndDateRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    .textField {
        margin: 0;
    }
`
